import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as HappyBuddy } from '../../../../assets/icons/v3/happy-buddy.svg';
import { ReactComponent as SurprisedBuddy } from '../../../../assets/icons/v3/surprised-buddy.svg';
import CardStatus from '../../../../shared/components/CardStatus/CardStatus';
import ActionBar from '../../../../shared/components/v3/ActionBar/ActionBar';
import BdsModal from '../../../../shared/components/v3/Modals/BdsModal/BdsModal';
import StyledTable from '../../../../shared/components/v3/Table/StyledTable/StyledTable';
import TablePaginationV3 from '../../../../shared/components/v3/TablePagination/TablePagination';
import Toast from '../../../../shared/components/v3/Toast/Toast';
import { COLLABORATOR_DETAIL_ROUTE, MANAGE_COLLABORATORS_ROUTE } from '../../../../shared/constants/auth';
import {
  COLLABORATORS_TABLE_ACTIONS,
  COLLABORATORS_TABLE_INITIAL_SORT,
  MAX_COLLABORATORS
} from '../../../../shared/constants/collaborators';
import { DEFAULT_PAGINATOR_STATE_V3, DEFAULT_PAGINATOR_OPTIONS_V3 } from '../../../../shared/constants/constants';
import {
  CANCEL_DELETE_COLLABORATOR,
  CLICK_ACTION,
  COLLABORATOR_DETAIL,
  CONFIRM_DELETE_COLLABORATOR,
  DELETE_COLLABORATOR,
  MANAGE_COLLABORATORS,
  RESEND_EMAIL,
  SECTION_COLLABORATORS
} from '../../../../shared/constants/tagManager';
import {
  DEFAULT_TOAST_DATA,
  DELETE_COLLABORATORS_ERROR_TOAST,
  DELETE_COLLABORATORS_SUCCESS_TOAST,
  DELETE_COLLABORATOR_SUCCESS_TOAST,
  RESEND_EMAIL_ERROR_TOAST,
  RESEND_EMAIL_SUCCESS_TOAST
} from '../../../../shared/constants/toast';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { getCollaboratorsTexts } from '../../../../shared/handlers/dataHandlers';
import { COLLABORATORS_TABLE_COLUMNS } from '../../../../shared/handlers/tableColumns';
import { useIsMounted } from '../../../../shared/hooks/useIsMounted';
import { IBdsModal } from '../../../../shared/interfaces/bdsModal';
import { IMembersData } from '../../../../shared/interfaces/businessData';
import { IMembersResponse } from '../../../../shared/interfaces/responseInterface';
import { ISelectedMap, ITableFilter } from '../../../../shared/interfaces/tables';
import { IToast } from '../../../../shared/interfaces/toast';
import Companies from '../../../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_COLLABORATORS } from '../../../../shared/translations/collaborators';
import { TRANSLATIONS_COLLABORATORS_DETAIL } from '../../../../shared/translations/collaboratorsDetail';
import { ModalType } from '../../../../shared/types/collaboratorsManagementTypes';
import { sortArray } from '../../../../shared/utils/utils';
import { isEmptyObject } from '../../../../shared/utils/validation/validation';
import TableFilter from '../TableFilter/TableFilter';

import './CollaboratorsTable.scss';

const CollaboratorsTable = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [collaborators, setCollaborators] = useState<IMembersData[]>([]);
  const [paginatorState, setPaginatorState] = useState<number>(DEFAULT_PAGINATOR_STATE_V3);
  const [actualPage, setActualPage] = useState<number>(0);
  const [currentPageData, setCurrentPageData] = useState<IMembersData[]>([]);
  const [selectedMap, setSelectedMap] = useState<ISelectedMap>({});
  const [allSelected, setAllSelected] = useState(false);
  const [toastData, setToastData] = useState<IToast>(DEFAULT_TOAST_DATA);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [totalSelected, setTotalSelected] = useState<number>(0);
  const [error, setError] = useState(false);
  const [filterSelected, setFilterSelected] = useState<ITableFilter>();
  const [activeCollaborator, setActiveCollaborator] = useState<IMembersData>();
  const [scrollDown, setScrollDown] = useState<boolean>(false);
  const { company } = useContext(CompanyContext);
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const { deleteModalTitle, deleteModalDescription, resendEmailsTitle, resendEmailsDescription } = useMemo(
    () => getCollaboratorsTexts(activeCollaborator ? 1 : totalSelected),
    [totalSelected, activeCollaborator]
  );

  const actionBarText = useMemo(
    () => t(TRANSLATION_COLLABORATORS.table.actionBar, { count: totalSelected, items: totalSelected }),
    [t, totalSelected]
  );

  const filters = useMemo(
    () => [
      { name: t(TRANSLATION_COLLABORATORS.table.filterAll), value: 'all' },
      { name: t(TRANSLATION_COLLABORATORS.table.filterActive), value: 'active' },
      { name: t(TRANSLATION_COLLABORATORS.table.filterInvited), value: 'invited' }
    ],
    [t]
  );

  const onSearch = useCallback(
    async (searchValue?: string, status?: string) => {
      setLoading(true);
      try {
        let response: IMembersResponse;
        setError(false);
        setFilterSelected({ searchValue, filterValue: status });
        if (company.id !== '') {
          response = await Companies.getFilteredMembers(company.id, searchValue?.toLowerCase(), status);
          if (isMounted) {
            const sorted = [...response.members].sort((a, b) => a.email.localeCompare(b.email));
            setCollaborators(sorted);
          }
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [company.id, isMounted]
  );

  const paginateCollaborators = useCallback(() => {
    const from = actualPage * paginatorState;
    const to = from + paginatorState;
    const pageData = collaborators?.slice(from, to);
    setCurrentPageData(pageData || []);
    setLoading(false);
  }, [actualPage, paginatorState, collaborators]);

  const onCheckAll = useCallback(() => {
    if (allSelected) {
      setSelectedMap({});
    } else {
      const newMap = collaborators?.reduce((map: ISelectedMap, obj) => {
        map[obj.email] = true;
        return map;
      }, {});
      setSelectedMap(newMap ?? {});
    }
    setAllSelected(!allSelected);
  }, [allSelected, collaborators]);

  const onDeleteCollaborator = useCallback((memberEmail: string) => {
    if (!memberEmail) {
      setSelectedMap({});
    }
    setCollaborators((prev) => prev.filter((collaborator) => collaborator.email !== memberEmail));
    setToastData(DELETE_COLLABORATOR_SUCCESS_TOAST);
    setOpenToast(true);
  }, []);

  const cleanActions = useCallback(() => {
    setModalType(null);
    if (!activeCollaborator) setSelectedMap({});
    if (!activeCollaborator) setTotalSelected(0);
    setOpenToast(true);
    setLoading(false);
  }, [activeCollaborator]);

  const onDeleteCollaborators = useCallback(async () => {
    setLoading(true);
    try {
      const selectedCollaborators = Object.keys(selectedMap).filter((key) => selectedMap[key]);
      await Companies.deleteMembers(company.id, selectedCollaborators);
      setCollaborators((prev) => prev.filter((collaborator) => !selectedCollaborators.includes(collaborator.email)));
      setToastData({
        ...DELETE_COLLABORATORS_SUCCESS_TOAST,
        title: t(DELETE_COLLABORATORS_SUCCESS_TOAST.title, { count: selectedCollaborators.length })
      });
      setAllSelected(false);
    } catch {
      setToastData(DELETE_COLLABORATORS_ERROR_TOAST);
      setError(true);
    } finally {
      cleanActions();
    }
  }, [company.id, selectedMap, t, cleanActions]);

  const resendEmails = useCallback(async () => {
    setLoading(true);
    try {
      const selectedCollaborators = activeCollaborator
        ? [activeCollaborator.email]
        : Object.keys(selectedMap).filter((key) => selectedMap[key]);
      await Companies.resendOnboardingCollaboratorEmails(company.id, selectedCollaborators);
      setToastData({
        ...RESEND_EMAIL_SUCCESS_TOAST,
        title: t(RESEND_EMAIL_SUCCESS_TOAST.title, { count: selectedCollaborators.length })
      });
    } catch {
      setToastData(RESEND_EMAIL_ERROR_TOAST);
    } finally {
      cleanActions();
    }
  }, [company.id, selectedMap, t, cleanActions, activeCollaborator]);

  const onCheckRow = useCallback((key: string) => {
    setSelectedMap((prevMap) => {
      const newMap = { ...prevMap };
      if (newMap[key]) {
        delete newMap[key];
      } else {
        newMap[key] = true;
      }
      return newMap;
    });
  }, []);

  const getModalsDescription = useCallback(
    (key: string) => {
      if (activeCollaborator) {
        return <Trans i18nKey={key} values={{ user_email: activeCollaborator.email }} components={{ b: <b /> }} />;
      }
      if (totalSelected > 1) {
        return t(key);
      }
      return <Trans i18nKey={key} values={{ user_email: Object.keys(selectedMap)[0] }} components={{ b: <b /> }} />;
    },
    [totalSelected, selectedMap, t, activeCollaborator]
  );

  const getModalProps = useCallback(
    (type: ModalType) => {
      switch (type) {
        case 'delete':
          return {
            open: modalType === type,
            title: t(deleteModalTitle, { users: totalSelected }),
            description: getModalsDescription(deleteModalDescription),
            icon: <SurprisedBuddy />,
            loading: loading,
            primaryAction: {
              label: t(TRANSLATION_COLLABORATORS.table.deleteModal.buttonConfirm),
              onClick: () => {
                TagManagerService.pushEvent(CLICK_ACTION, CONFIRM_DELETE_COLLABORATOR);
                onDeleteCollaborators();
              }
            },
            secondaryAction: {
              label: t(TRANSLATION_COLLABORATORS.table.deleteModal.buttonClose),
              onClick: () => {
                TagManagerService.pushEvent(CLICK_ACTION, CANCEL_DELETE_COLLABORATOR);
                setModalType(null);
              }
            }
          };
        case 'resendEmail':
          return {
            open: modalType === type,
            title: t(resendEmailsTitle),
            description: getModalsDescription(resendEmailsDescription),
            icon: <HappyBuddy />,
            loading: loading,
            primaryAction: {
              label: t(TRANSLATION_COLLABORATORS.table.resendEmailModal.buttonSend, { count: totalSelected }),
              onClick: resendEmails
            },
            onClose: () => setModalType(null)
          };
      }
    },
    [
      modalType,
      t,
      deleteModalTitle,
      deleteModalDescription,
      resendEmailsTitle,
      resendEmailsDescription,
      loading,
      totalSelected,
      getModalsDescription,
      onDeleteCollaborators,
      resendEmails
    ]
  );

  const sortData = (columnKey: string, isAscending: boolean) => {
    sortArray(collaborators, columnKey, isAscending);
    if (collaborators) paginateCollaborators();
  };

  const totalCollaborators = useMemo(() => {
    return collaborators ? collaborators.length : 0;
  }, [collaborators]);

  const closeActionBar = useCallback(() => {
    setSelectedMap({});
    setAllSelected(false);
    setTotalSelected(0);
  }, []);

  const handleActionBarOptions = useCallback((option: string) => {
    switch (option) {
      case 'delete':
        TagManagerService.pushEvent(CLICK_ACTION, DELETE_COLLABORATOR);
        setModalType('delete');
        break;
      case 'resendEmail':
        TagManagerService.pushEvent(CLICK_ACTION, RESEND_EMAIL);
        setModalType('resendEmail');
        break;
    }
  }, []);

  const onRowClick = (collaborator: IMembersData) => {
    TagManagerService.pushEvent(CLICK_ACTION, COLLABORATOR_DETAIL);
    setActiveCollaborator(collaborator);
    navigate(COLLABORATOR_DETAIL_ROUTE, { state: { collaborator } });
  };

  const goToManageCollaborators = () => {
    navigate(MANAGE_COLLABORATORS_ROUTE);
    TagManagerService.pushEvent(CLICK_ACTION, MANAGE_COLLABORATORS);
  };

  useEffect(() => {
    setActualPage(0);
  }, [collaborators, paginatorState]);

  useEffect(() => {
    paginateCollaborators();
  }, [collaborators]);

  useEffect(() => {
    paginateCollaborators();
  }, [actualPage, paginatorState]);

  useEffect(() => {
    if (isMounted) {
      setSelectedMap({});
      setAllSelected(false);
      onSearch();
    }
  }, [company, isMounted]);

  useEffect(() => {
    const selected = Object.values(selectedMap).filter(Boolean).length;
    setTotalSelected(selected);
    setAllSelected(selected === collaborators.length && selected > 0);
  }, [selectedMap, collaborators]);

  useEffect(() => {
    if (scrollDown) {
      window.scrollTo({ top: document.body.scrollHeight });
      setScrollDown(false);
    }
  }, [currentPageData]);

  const getActionBarProps = () => {
    const collabsSelected = Object.keys(selectedMap).filter((key) => selectedMap[key]);
    const newCollaborators = currentPageData.filter((collaborator) => collabsSelected.includes(collaborator.email));

    const emailAction = newCollaborators.map((collab) => {
      if (collab.status === COLLABORATORS_TABLE_ACTIONS.invitedStatus) {
        return COLLABORATORS_TABLE_ACTIONS.isInvited;
      } else {
        return undefined;
      }
    });

    return {
      text: actionBarText,
      onPressDelete: () => handleActionBarOptions(COLLABORATORS_TABLE_ACTIONS.delete),
      onPressX: closeActionBar,
      ...(emailAction.includes(COLLABORATORS_TABLE_ACTIONS.isInvited)
        ? { onPressEmail: () => handleActionBarOptions(COLLABORATORS_TABLE_ACTIONS.resendEmail) }
        : undefined)
    };
  };

  return (
    <>
      <div className="collaborators-home">
        <TableFilter
          className={`collaborators-table-filter ${currentPageData.length ? '' : 'empty'}`}
          onSearch={onSearch}
          loading={loading}
          filters={filters}
        />
        <StyledTable
          data={currentPageData}
          loading={loading}
          top={112}
          columns={COLLABORATORS_TABLE_COLUMNS}
          activeRow={activeCollaborator ? activeCollaborator.email : undefined}
          onRowClick={onRowClick}
          onChange={onDeleteCollaborator}
          dataKey="email"
          error={error}
          sortData={sortData}
          initialSortState={COLLABORATORS_TABLE_INITIAL_SORT}
          emptyCard={{
            primaryText: TRANSLATION_COLLABORATORS.table.emptyData.activeTable.title,
            secondaryText: TRANSLATION_COLLABORATORS.table.emptyData.activeTable.description,
            buttonText: TRANSLATION_COLLABORATORS.table.emptyData.activeTable.button,
            action: goToManageCollaborators
          }}
          filters={filterSelected}
          noFilterDataCard={{
            primaryText: TRANSLATION_COLLABORATORS.table.noResults.title,
            secondaryText: TRANSLATION_COLLABORATORS.table.noResults.description
          }}
          checkProps={{ onCheckRow, onCheckAll, checkedRows: selectedMap, allSelected }}
        />
        {collaborators && collaborators.length >= MAX_COLLABORATORS && (
          <div className="info-row">
            <CardStatus type="informative" message={t(TRANSLATIONS_COLLABORATORS_DETAIL.maxMembersInfo)} />
          </div>
        )}
        {collaborators && (
          <div className="paginator-container">
            <TablePaginationV3
              rowsPerPageOptions={DEFAULT_PAGINATOR_OPTIONS_V3}
              rowsPerPage={paginatorState}
              count={totalCollaborators}
              actualPage={actualPage}
              onPageChange={(e) => {
                setActualPage(e);
                setScrollDown(true);
              }}
              onRowsPerPageChange={(e) => {
                setPaginatorState(e);
                setScrollDown(true);
              }}
              sectionPage={SECTION_COLLABORATORS}
              marginTop={68}
            />
          </div>
        )}
        {selectedMap && !isEmptyObject(selectedMap) && (
          <div className="collaborators-action-bar">
            <ActionBar {...getActionBarProps()} />
          </div>
        )}
      </div>
      {getModalProps('delete') && <BdsModal {...(getModalProps('delete') as IBdsModal)} />}
      {getModalProps('resendEmail') && <BdsModal {...(getModalProps('resendEmail') as IBdsModal)} />}
      {openToast && (
        <Toast
          title={t(toastData.title)}
          icon={toastData.icon}
          open={openToast}
          button={toastData.button}
          handleClose={() => setOpenToast(false)}
        />
      )}
    </>
  );
};

export default React.memo(CollaboratorsTable);
